/**
 * Fleet Dashboard Module
 *
 * Main container for fleet-wide oversight functionality.
 * Provides shore-side users with overview of all vessels.
 *
 * Features:
 * - Fleet Overview with RAG status per vessel
 * - Notification Center
 * - Certificate Dashboard
 * - Basic Reports
 *
 * @see FLEET-DASHBOARD-DESIGN.md
 */

import React, { useState } from 'react';
import {
  Ship,
  Bell,
  FileCheck,
  FileText,
  RefreshCw,
  Plus,
  AlertTriangle,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

import { useFleetData } from '../../../../hooks/domain/fleet/useFleetData';
import { useFleetNotifications } from '../../../../hooks/domain/fleet/useFleetNotifications';
import { usePermissions } from '../../../../hooks/usePermissions';

import FleetOverview from './FleetOverview';
import NotificationCenter from './NotificationCenter';
import CertificateDashboard from './CertificateDashboard';
import FleetReports from './FleetReports';

/**
 * Fleet Dashboard Main Component
 */
const FleetDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { isFleet, currentUser } = usePermissions();
  const {
    fleetSummary,
    isLoading,
    lastRefresh,
    refreshFleetData
  } = useFleetData();
  const { unacknowledgedCount } = useFleetNotifications();

  // Tabs configuration
  const tabs = [
    {
      id: 'overview',
      label: 'Fleet Overview',
      icon: Ship,
      badge: fleetSummary.actionRequired > 0 ? fleetSummary.actionRequired : null,
      badgeColor: 'red'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      badge: unacknowledgedCount > 0 ? unacknowledgedCount : null,
      badgeColor: 'amber'
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: FileCheck,
      badge: fleetSummary.totalExpiringCerts > 0 ? fleetSummary.totalExpiringCerts : null,
      badgeColor: 'amber'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: FileText,
      badge: null
    }
  ];

  // Access check
  if (!isFleet) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-amber-800 mb-2">
            Fleet Access Required
          </h2>
          <p className="text-amber-700">
            The Fleet Dashboard is only available to users with fleet management permissions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Ship className="h-6 w-6" />
              Fleet Dashboard
            </h1>
            <p className="text-slate-300 text-sm mt-1">
              {currentUser?.name || 'Fleet Manager'} - Shore Access
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Fleet Summary Stats */}
            <div className="hidden md:flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span>{fleetSummary.compliant} Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-400" />
                <span>{fleetSummary.attention} Attention</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span>{fleetSummary.actionRequired} Action Required</span>
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={refreshFleetData}
              disabled={isLoading}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              title={lastRefresh ? `Last refreshed: ${new Date(lastRefresh).toLocaleTimeString()}` : 'Refresh'}
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium
                border-b-2 transition-colors relative
                ${activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge && (
                <span className={`
                  absolute -top-1 -right-1 min-w-[20px] h-5
                  flex items-center justify-center
                  text-xs font-bold rounded-full px-1.5
                  ${tab.badgeColor === 'red' ? 'bg-red-500 text-white' : ''}
                  ${tab.badgeColor === 'amber' ? 'bg-amber-500 text-white' : ''}
                `}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto bg-slate-50">
        {activeTab === 'overview' && <FleetOverview />}
        {activeTab === 'notifications' && <NotificationCenter />}
        {activeTab === 'certificates' && <CertificateDashboard />}
        {activeTab === 'reports' && <FleetReports />}
      </div>
    </div>
  );
};

export default FleetDashboard;
